import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaPrint } from 'react-icons/fa';
import { IoCloseOutline } from 'react-icons/io5';
import { formatMobileDisplayOrFallback } from './formatDisplay';
import './css/OrderConfirmation.css';

const OrderConfirmation = ({ orderData, onClose }) => {
    const navigate = useNavigate();

    if (!orderData) {
        return null;
    }

    const handlePrintReceipt = () => {
        // Navigate to receipt page
        navigate('/receipt', { state: { orderData } });
    };

    const handleCancel = () => {
        onClose?.();
        const tab = orderData?.returnTab || 'orders';
        navigate(`/home?tab=${tab}`);
    };

    const handleBackdropClick = (e) => {
        // Close popup if clicking on backdrop (not on the container)
        if (e.target === e.currentTarget) {
            handleCancel();
        }
    };

    if (!orderData) {
        return <div>Loading...</div>;
    }

    const { orderId, orderNumber, mobile, tableNumber, paymentMethod, items, subtotal, gst, total, gstPercentage } = orderData;
    
    // Calculate SGST and Service Charge (5% each of subtotal)
    const sgst = subtotal * 0.05;
    const serviceCharge = subtotal * 0.05;

    return (
        <div className="order-confirmation-page" onClick={handleBackdropClick}>
            <div className="confirmation-container" onClick={(e) => e.stopPropagation()}>
                {/* Cancel Button */}
                <button className="cancel-button" onClick={handleCancel} title="Close">
                    <IoCloseOutline />
                </button>
                {/* Order Receipt Card */}
                <div className="order-receipt-card">
                {/* Header with Checkmark */}
                <div className="confirmation-header">
                    <div className="checkmark-icon">
                        <FaCheckCircle />
                    </div>
                    <h1 className="confirmation-title">
                        {orderData.isNewOrderPlaced ? 'Order placed successfully!' : 'Order updated successfully!'}
                    </h1>
                </div>

                    <div className="receipt-header">
                        <div className="receipt-divider"></div>
                    </div>

                    {/* Order Details */}
                    <div className="order-details-section">
                        <div className="detail-row">
                            <span className="detail-label">Order Id:</span>
                            <span className="detail-value">{orderId || orderNumber || 'N/A'}</span>
                            <span className="detail-label payment-method-label">Payment Method:</span>
                            <span className="detail-value">
                                {paymentMethod 
                                    ? (Array.isArray(paymentMethod) 
                                        ? paymentMethod.map(p => String(p).toUpperCase()).join(', ')
                                        : String(paymentMethod).toUpperCase())
                                    : 'N/A'}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Table Number:</span>
                            <span className="detail-value">{tableNumber ? `Table ${tableNumber}` : 'N/A'}</span>
                            <span className="detail-label" style={{ marginLeft: '20px' }}>Mobile:</span>
                            <span className="detail-value">{formatMobileDisplayOrFallback(mobile)}</span>
                        </div>
                    </div>

                    {/* Ordered Items */}
                    <div className="ordered-items-section">
                        <h3 className="items-heading">Ordered Items:</h3>
                        <table className="items-table">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Quantity</th>
                                    <th>Price</th>
                                    <th>Subtotal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items && items.length > 0 ? (
                                    items.map((item, index) => (
                                        <tr key={index}>
                                            <td className="item-name">{item.foodItemName || item.name}</td>
                                            <td className="item-quantity">{item.foodQuantity || item.quantity}</td>
                                            <td className="item-price">₹{parseFloat(item.price || 0).toFixed(2)}</td>
                                            <td className="item-subtotal">₹{((item.foodQuantity || item.quantity) * parseFloat(item.price || 0)).toFixed(2)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="no-items">No items found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Summary Section */}
                    <div className="summary-section">
                        <div className="summary-row">
                            <span className="summary-label">Sub Total:</span>
                            <span className="summary-value">₹{subtotal.toFixed(2)}</span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">GST@2.5:</span>
                            <span className="summary-value">₹{gst.toFixed(2)}</span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">SGST:</span>
                            <span className="summary-value">₹{sgst.toFixed(2)}</span>
                        </div>
                        <div className="summary-row">
                            <span className="summary-label">Service Charge:</span>
                            <span className="summary-value">₹{serviceCharge.toFixed(2)}</span>
                        </div>
                        <div className="summary-divider"></div>
                        <div className="summary-row total-row">
                            <span className="summary-label">Total Amount:</span>
                            <span className="summary-value total-amount">₹{total.toFixed(2)}</span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="confirmation-footer">
                    <p className="thank-you-message">Thank you for your order!</p>
                    <button className="print-receipt-button" onClick={handlePrintReceipt}>
                        <FaPrint className="print-icon" />
                        Print Receipt
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrderConfirmation;
